import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown } from 'lucide-react';
import { motion } from 'motion/react';
import { SelectPopover } from './SelectPopover';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps {
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  className?: string; // For the trigger button
  id?: string;
  title?: string;
}

export function Select({ value, onChange, options, placeholder = "Select...", className = "", id, title }: SelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!isOpen) return;
      
      const target = event.target as Node;
      
      // If clicking on the trigger container, let the onClick handler deal with it
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }
      
      // If the click is inside the dropdown content or the portal wrapper itself (like clicking the scrollbar)
      const targetElement = target as Element;
      const portalContent = targetElement.closest ? targetElement.closest('.responsive-overlay-portal') : null;
      
      if (
        (dropdownRef.current && dropdownRef.current.contains(target)) ||
        (portalContent && dropdownRef.current && portalContent.contains(dropdownRef.current))
      ) {
        return;
      }
      
      setIsOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  useLayoutEffect(() => {
    if (!isOpen) return;
    
    let isMobileSelect = false;
    if (window.innerWidth < 640) {
      isMobileSelect = true;
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      if (isMobileSelect) {
        document.body.style.overflow = '';
      }
    };
  }, [isOpen]);

  const selectedOption = options.find(o => o.value === value);

  return (
    <div className="flex flex-col gap-2 w-full" ref={containerRef}>
      <div className="relative">
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
          className={`w-full flex items-center justify-between text-sm border border-slate-200 dark:border-slate-800 rounded-lg px-3 py-2 bg-white/50 dark:bg-slate-900/50 hover:bg-slate-100 dark:hover:bg-slate-900 focus:bg-slate-100 dark:focus:bg-slate-900 dark:shadow-inner text-slate-700 dark:text-slate-300 focus:border-slate-200 dark:focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 outline-none cursor-pointer ${className}`}
        >
          <span className="truncate">{selectedOption ? selectedOption.label : <span className="opacity-50">{placeholder}</span>}</span>
          <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        <SelectPopover
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          options={options}
          value={value}
          onChange={onChange}
          title={title}
          placeholder={placeholder}
          dropdownRef={dropdownRef}
        />
      </div>
    </div>
  );
}

